import {
  createFamilyMemberRows,
  type FamilyMember,
} from "@project/admin/components/family-details-constants";
import type {
  ElderAdmissionPayload,
  ElderAdmissionRecord,
} from "@project/types/elder-admission";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

export type AdmissionFormState = {
  applicantName: string;
  applicantMobile: string;
  applicantMobileCountry: PhoneCountryCode;
  applicantAddress: string;
  relationshipWithElder: string;
  elderName: string;
  fatherName: string;
  age: string;
  currentLivingSituation: string;
  physicalCondition: string;
  reasonForAdmission: string;
  pensionDetails: string;
  otherIncome: string;
  emergencyContact1: string;
  emergencyContact2: string;
  emergencyContact3: string;
  additionalNotes: string;
};

export function admissionRecordToFormState(
  record: ElderAdmissionRecord
): AdmissionFormState {
  return {
    applicantName: record.applicant_name ?? "",
    applicantMobile: record.applicant_mobile ?? "",
    applicantMobileCountry: DEFAULT_PHONE_COUNTRY,
    applicantAddress: record.applicant_address ?? "",
    relationshipWithElder: String(record.relationship_with_elder),
    elderName: record.elder_name ?? "",
    fatherName: record.father_name ?? "",
    age: String(record.age),
    currentLivingSituation: record.current_living_situation ?? "",
    physicalCondition: record.physical_condition ?? "",
    reasonForAdmission: record.reason_for_admission ?? "",
    pensionDetails: record.pension_details ?? "",
    otherIncome: record.other_income ?? "",
    emergencyContact1: record.emergency_contact_1 ?? "",
    emergencyContact2: record.emergency_contact_2 ?? "",
    emergencyContact3: record.emergency_contact_3 ?? "",
    additionalNotes: record.additional_notes ?? "",
  };
}

export function admissionRecordToFamilyMembers(
  record: ElderAdmissionRecord
): FamilyMember[] {
  const members = record.family_members ?? [];
  if (members.length === 0) {
    return createFamilyMemberRows(3);
  }
  return members.map(member => ({
    rowId: crypto.randomUUID(),
    name: member.name ?? "",
    relationship: String(member.relationship),
    mobile: member.mobile_number ?? "",
    address: member.address ?? "",
    occupation: member.occupation ?? "",
    income: member.income === null ? "" : String(member.income ?? ""),
    emergencyContact: member.is_emergency_contact,
  }));
}

export function buildAdmissionPayload(
  form: AdmissionFormState,
  location: { districtId: string; cityId: string; areaId: string },
  familyMembers: FamilyMember[]
): ElderAdmissionPayload {
  const family_members = familyMembers
    .filter(row => row.name.trim())
    .map(row => ({
      name: row.name.trim(),
      relationship: Number(row.relationship),
      mobile_number: row.mobile.trim(),
      address: row.address.trim() || undefined,
      occupation: row.occupation.trim() || undefined,
      income: row.income.trim() ? row.income.trim() : null,
      is_emergency_contact: row.emergencyContact,
    }));

  return {
    applicant_name: form.applicantName.trim(),
    applicant_mobile: form.applicantMobile.trim(),
    applicant_address: form.applicantAddress.trim(),
    relationship_with_elder: Number(form.relationshipWithElder),
    elder_name: form.elderName.trim(),
    father_name: form.fatherName.trim() || undefined,
    age: Number(form.age),
    district: Number(location.districtId),
    city: Number(location.cityId),
    area: Number(location.areaId),
    current_living_situation: form.currentLivingSituation.trim() || undefined,
    physical_condition: form.physicalCondition.trim() || undefined,
    reason_for_admission: form.reasonForAdmission.trim(),
    pension_details: form.pensionDetails.trim() || undefined,
    other_income: form.otherIncome.trim() || undefined,
    emergency_contact_1: form.emergencyContact1.trim(),
    emergency_contact_2: form.emergencyContact2.trim() || null,
    emergency_contact_3: form.emergencyContact3.trim() || null,
    additional_notes: form.additionalNotes.trim() || undefined,
    family_members,
  };
}

// Validate only the fields that belong to a single step. Returns an i18n
// message key (under `account.admissionForm.validation`) for the first invalid
// field on that step, or null when the step is valid. Used to block the
// "Next" button and surface a message as the user moves through the wizard.
export function validateAdmissionStep(
  step: number,
  form: AdmissionFormState,
  location: { districtId: string; cityId: string; areaId: string }
): string | null {
  const key = (k: string) => `account.admissionForm.validation.${k}`;

  if (step === 0) {
    if (!form.applicantName.trim()) {
      return key("applicantNameRequired");
    }
    if (!form.applicantMobile.trim()) {
      return key("applicantMobileRequired");
    }
    if (
      !isValidMobileNumber(form.applicantMobile, form.applicantMobileCountry)
    ) {
      return key("applicantMobileInvalid");
    }
    if (!form.applicantAddress.trim()) {
      return key("applicantAddressRequired");
    }
    if (!form.relationshipWithElder) {
      return key("relationshipRequired");
    }
  }

  if (step === 1) {
    if (!form.elderName.trim()) {
      return key("elderNameRequired");
    }
    if (!form.age.trim() || Number.isNaN(Number(form.age))) {
      return key("ageRequired");
    }
    if (!location.districtId) {
      return key("districtRequired");
    }
    if (!location.cityId) {
      return key("cityRequired");
    }
    if (!location.areaId) {
      return key("areaRequired");
    }
    if (!form.reasonForAdmission.trim()) {
      return key("reasonRequired");
    }
  }

  if (step === 3) {
    if (!form.emergencyContact1.trim()) {
      return key("emergencyContactRequired");
    }
  }

  return null;
}

// Returns an i18n key (under `account.admissionForm.validation`) rather than a
// literal message so the caller can localize it with `t(...)`.
export function validateAdmissionForm(
  form: AdmissionFormState,
  location: { districtId: string; cityId: string; areaId: string }
): { step: number; messageKey: string } | null {
  const key = (k: string) => `account.admissionForm.validation.${k}`;

  if (!form.applicantName.trim()) {
    return { step: 0, messageKey: key("applicantNameRequired") };
  }
  if (!form.applicantMobile.trim()) {
    return { step: 0, messageKey: key("applicantMobileRequired") };
  }
  if (!isValidMobileNumber(form.applicantMobile, form.applicantMobileCountry)) {
    return { step: 0, messageKey: key("applicantMobileInvalid") };
  }
  if (!form.applicantAddress.trim()) {
    return { step: 0, messageKey: key("applicantAddressRequired") };
  }
  if (!form.relationshipWithElder) {
    return { step: 0, messageKey: key("relationshipRequired") };
  }

  if (!form.elderName.trim()) {
    return { step: 1, messageKey: key("elderNameRequired") };
  }
  if (!form.age.trim() || Number.isNaN(Number(form.age))) {
    return { step: 1, messageKey: key("ageRequired") };
  }
  if (!location.districtId) {
    return { step: 1, messageKey: key("districtRequired") };
  }
  if (!location.cityId) {
    return { step: 1, messageKey: key("cityRequired") };
  }
  if (!location.areaId) {
    return { step: 1, messageKey: key("areaRequired") };
  }
  if (!form.reasonForAdmission.trim()) {
    return { step: 1, messageKey: key("reasonRequired") };
  }

  if (!form.emergencyContact1.trim()) {
    return { step: 3, messageKey: key("emergencyContactRequired") };
  }

  return null;
}
